import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./EclipsePaywall.module.scss"

// ─────────────────────────────────────────────────────────────────────────────
// "Eclipse" — an ONBOARDING paywall built on CONTRAST FRAMING via a single
// continuous mechanic: SLIDE THE WORLD INTO TOTALITY. A brand-new user's photos
// still live in the ordinary library, where the camera roll, shared albums,
// photo-picker apps and anyone who grabs the phone can all see in. This screen
// stages that as an ANNULAR ECLIPSE ("ring of fire"): drag from "Open to all"
// toward "Only you" and one by one the outside watchers lose sight and drift
// away, the sky goes to totality, and a warm corona ignites around your photos —
// which stay fully lit the entire time. Everyone else loses sight; you never do.
//
// The interaction is a horizontal DRAG-TO-TOTALITY scrub (a continuous 0→1
// phase), not a scrub-list, hold, flip, dial, pour, press or typed input. The
// visual metaphor is celestial (eclipse / rim-light), the emotional register is
// awe + hush + intimacy, and the value frame reaches the security-and-privacy
// directive through the honest gap between "who can see this now" and "only you"
// — never through fear, countdowns or lock hardware.
//
// Honest & App-Store compliant: the featured, most-prominent price is the yearly
// plan (7-day free trial, per-month broken out, real Save %); monthly is a small
// secondary line. No countdown, no scarcity, no data-loss claim — the photos are
// the one thing that stays lit; the eclipse only hides them from everyone else,
// nothing is deleted or locked away from you. Reuses only the house fonts, colour
// tokens and bundled memory photos. Self-contained: renders on debug data, no
// native injection required.
// ─────────────────────────────────────────────────────────────────────────────

const PUB = process.env.PUBLIC_URL ?? ""
// Reuse the bundled everyday-memory photos — no runtime egress.
const photo = (n: string) => `${PUB}/byheart/${n}.jpg`

// The little pile of "your photos" that sits inside the corona, always lit.
const PILE = [
  { src: photo("m07"), tilt: -9, x: -20, y: 6, z: 1 },
  { src: photo("m02"), tilt: 8, x: 20, y: 3, z: 2 },
  { src: photo("m05"), tilt: -1, x: 0, y: -6, z: 3 },
]

// Who can see into an ordinary photo library — all honestly true for photos that
// simply sit in the camera roll. Angles place them around the top of the ring so
// they can drift outward and dim as the eclipse reaches totality.
const WATCHERS: { label: string; angle: number; icon: JSX.Element }[] = [
  {
    label: "Your camera roll",
    angle: -128,
    icon: (
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
        <rect
          x="3.5"
          y="6.5"
          width="17"
          height="12"
          rx="2.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle
          cx="12"
          cy="12.5"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M8.5 6.5l1.2-2h4.6l1.2 2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
      </svg>
    ),
  },
  {
    label: "Shared albums",
    angle: -74,
    icon: (
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
        <circle
          cx="8.5"
          cy="9"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle
          cx="16"
          cy="10.5"
          r="2.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <path
          d="M3.5 19c0-2.8 2.2-4.6 5-4.6s5 1.8 5 4.6M14 18.4c.2-2 1.6-3.3 3.8-3.3 2.2 0 3.7 1.4 3.7 3.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    label: "Photo-picker apps",
    angle: -20,
    icon: (
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
        <rect
          x="5"
          y="3.5"
          width="14"
          height="17"
          rx="2.6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <circle cx="12" cy="17.4" r="1.3" fill="currentColor" />
        <path
          d="M9 8.5h6M9 11.5h6"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </svg>
    ),
  },
  {
    label: "Anyone holding your phone",
    angle: 34,
    icon: (
      <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden>
        <path
          d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinejoin="round"
        />
        <circle
          cx="12"
          cy="12"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </svg>
    ),
  },
]

const clamp01 = (n: number) => (n < 0 ? 0 : n > 1 ? 1 : n)
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3)

function EclipsePaywall() {
  const { state, actions } = useAppState()
  const reduce = useReducedMotion()

  const topInset = state.initialData?.topInset ?? 0
  const bottomInset = state.initialData?.bottomInset ?? 0
  const darkMode = state.initialData?.darkMode ?? false

  const premium = state.productData?.premium
  const yearly = premium?.yearlyProduct
  const monthly = premium?.monthlyProduct
  const locale = yearly?.priceLocale

  const yearlyPrice = yearly ? formatCurrency(yearly.price, locale) : ""
  const perMonth = yearly ? formatCurrency(yearly.price / 12, locale) : ""
  const monthlyPrice = monthly ? formatCurrency(monthly.price, locale) : ""
  const savings = useMemo(() => {
    if (!yearly || !monthly) return 0
    return Math.round((1 - yearly.price / (monthly.price * 12)) * 100)
  }, [yearly, monthly])

  // phase: 0 = open to everyone, 1 = totality / only you
  const [phase, setPhase] = useState(reduce ? 1 : 0)
  const [plan, setPlan] = useState<"yearly" | "monthly">("yearly")
  const [showClose, setShowClose] = useState(false)
  const [committed, setCommitted] = useState(false)

  const trackRef = useRef<HTMLDivElement>(null)
  const rafRef = useRef<number | null>(null)
  const timers = useRef<number[]>([])
  const touchedRef = useRef(false) // user grabbed the slider — stop auto-run

  const push = (fn: () => void, ms: number) =>
    timers.current.push(window.setTimeout(fn, ms))

  // The stage is a fixed planetarium-dark surface in both themes, so take over
  // the safe-area insets and paint them to match the sky (no light bars).
  useEffect(() => {
    actions.setIgnoreHeaderInset(true)
    actions.setIgnoreFooterInset(true)
    actions.setHeaderInsetColor(darkMode ? "#0b0d1c" : "#232850")
    actions.setFooterInsetColor(darkMode ? "#04040c" : "#101126")
  }, [actions, darkMode])

  // On load: reveal the close affordance, then auto-slide the sky into totality
  // so passive viewers and screenshots land on the sealed "only you" state.
  useEffect(() => {
    push(() => setShowClose(true), reduce ? 0 : 1400)
    if (reduce) {
      setPhase(1)
      return () => timers.current.forEach(window.clearTimeout)
    }

    const DUR = 1750
    let start = 0
    const tick = (ts: number) => {
      if (touchedRef.current) return
      if (!start) start = ts
      const t = clamp01((ts - start) / DUR)
      setPhase(easeOutCubic(t))
      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }
    push(() => {
      if (!touchedRef.current) rafRef.current = requestAnimationFrame(tick)
    }, 700)

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      timers.current.forEach(window.clearTimeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduce])

  const setFromClientX = useCallback((clientX: number) => {
    const el = trackRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setPhase(clamp01((clientX - r.left) / r.width))
  }, [])

  const takeOver = useCallback(() => {
    touchedRef.current = true
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    takeOver()
    e.currentTarget.setPointerCapture?.(e.pointerId)
    setFromClientX(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (e.buttons === 0) return
    setFromClientX(e.clientX)
  }
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return
    takeOver()
    e.preventDefault()
    setPhase((p) => clamp01(p + (e.key === "ArrowRight" ? 0.12 : -0.12)))
  }

  const purchase = useCallback(() => {
    const product = plan === "yearly" ? yearly : monthly
    setCommitted(true)
    push(() => {
      if (product) native.subscribe(product)
    }, 720)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, yearly, monthly])

  const handleClose = () => native.postNativeEvent("close")

  // Derived, live from the phase.
  const sealed = phase > 0.985
  const watching = Math.max(0, Math.round((1 - phase) * WATCHERS.length))
  const pct = Math.round(phase * 100)

  const ctaSub =
    plan === "yearly"
      ? `${yearlyPrice}/year after your 7-day free trial — just ${perMonth}/mo. Renews yearly, cancel anytime.`
      : `${monthlyPrice}/month. Renews monthly, cancel anytime.`

  return (
    <div
      className={`${styles.root} ${sealed ? styles.isSealed : ""} ${
        committed ? styles.isCommitted : ""
      }`}
      style={{ ["--phase" as any]: phase }}
    >
      <div className={styles.sky} aria-hidden>
        <span className={styles.stars} />
        <span className={styles.starsFar} />
        <span className={styles.corona} />
      </div>

      <div
        className={styles.scroll}
        style={{ paddingTop: topInset + 12, paddingBottom: bottomInset + 14 }}
      >
        {/* Top bar — brand clarity */}
        <div className={styles.topbar}>
          <span className={styles.wordmark}>
            <span className={styles.wmMark} aria-hidden>
              <svg viewBox="0 0 24 24" width="12" height="12">
                <path
                  d="M8 10V7a4 4 0 0 1 8 0v3"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <rect
                  x="5.5"
                  y="10"
                  width="13"
                  height="9.5"
                  rx="2.4"
                  fill="currentColor"
                />
              </svg>
            </span>
            PRIVATE&nbsp;PHOTO&nbsp;VAULT
          </span>
          <button
            className={`${styles.close} ${showClose ? styles.closeOn : ""}`}
            onClick={handleClose}
            aria-label="Not now"
            tabIndex={showClose ? 0 : -1}
          >
            <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden>
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.9"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Headline */}
        <header className={styles.hero}>
          <p className={styles.eyebrow}>Welcome to your vault</p>
          <h1 className={styles.title}>
            Your photos are lit for the whole world.
            <br />
            <span className={styles.titleThen}>Seal them to only you.</span>
          </h1>
        </header>

        {/* ── The eclipse stage ─────────────────────────────────────────────── */}
        <section className={styles.stageWrap} aria-label="Slide into totality">
          <div className={styles.stage}>
            {/* watchers around the ring — dim & drift out as phase → 1 */}
            {WATCHERS.map((w, i) => (
              <span
                key={w.label}
                className={styles.watcher}
                style={{
                  ["--a" as any]: `${w.angle}deg`,
                  ["--d" as any]: i,
                }}
              >
                <span className={styles.watcherOrb}>{w.icon}</span>
                <span className={styles.watcherLabel}>{w.label}</span>
              </span>
            ))}

            {/* the ring of fire + your photos, always lit inside it */}
            <div className={styles.ring}>
              <span className={styles.ringFlare} aria-hidden />
              <span className={styles.moon} aria-hidden />
              <span className={styles.ringEdge} aria-hidden />

              <div className={styles.pile}>
                {PILE.map((p, i) => (
                  <span
                    key={i}
                    className={styles.card}
                    style={{
                      ["--tilt" as any]: `${p.tilt}deg`,
                      ["--px" as any]: `${p.x}px`,
                      ["--py" as any]: `${p.y}px`,
                      zIndex: p.z,
                    }}
                  >
                    <img src={p.src} alt="" draggable={false} loading="eager" />
                  </span>
                ))}
                <span className={`${styles.onlyYou} ${styles.onlyYouShow}`}>
                  <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden>
                    <path
                      d="M20 6L9 17l-5-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  Only you can see in
                </span>
              </div>
            </div>

            {/* live readout of who can see in */}
            <div className={styles.readout} aria-live="polite">
              {sealed ? (
                <span className={styles.readSealed}>
                  Sealed — visible to <b>only you</b>
                </span>
              ) : (
                <span className={styles.readOpen}>
                  Visible to <b>{watching}</b>{" "}
                  {watching === 1 ? "onlooker" : "onlookers"} right now
                </span>
              )}
            </div>
          </div>

          {/* ── drag-to-totality control ─────────────────────────────────── */}
          <div
            ref={trackRef}
            className={styles.track}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            role="slider"
            tabIndex={0}
            aria-label="Slide from open-to-all into totality"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={pct}
            aria-valuetext={sealed ? "Only you" : `${watching} onlookers`}
            onKeyDown={onKeyDown}
          >
            <span className={styles.trackFill} />
            <span className={styles.trackEndA}>Open to all</span>
            <span className={styles.trackEndB}>Only you</span>
            <span className={styles.knob}>
              <span className={styles.knobCore} />
            </span>
          </div>
        </section>

        {/* what "only you" actually means — real protections */}
        <ul className={styles.assurances} aria-label="What sealing gives you">
          <li>
            <span className={styles.aDot} />
            Opens only with your Face&nbsp;ID or&nbsp;PIN
          </li>
          <li>
            <span className={styles.aDot} />
            Kept out of your camera roll &amp; shared albums
          </li>
          <li>
            <span className={styles.aDot} />
            Private, encrypted backup on every device
          </li>
        </ul>

        {/* ── Plans — yearly is the hero ─────────────────────────────────── */}
        <section className={styles.plans} aria-label="Choose your plan">
          <button
            className={`${styles.plan} ${styles.planYear} ${
              plan === "yearly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("yearly")}
            aria-pressed={plan === "yearly"}
          >
            {savings > 0 && (
              <span className={styles.bestTag}>Save {savings}%</span>
            )}
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planName}>Sealed for the year</span>
                <span className={styles.planCovers}>
                  7-day free trial, then billed yearly
                </span>
              </span>
              <span className={styles.planPrice}>
                <span className={styles.priceRow}>
                  <span className={styles.priceBig}>{yearlyPrice}</span>
                  <span className={styles.pricePer}>/yr</span>
                </span>
                <span className={styles.pricePm}>{perMonth}/mo</span>
              </span>
            </span>
          </button>

          <button
            className={`${styles.plan} ${styles.planMonth} ${
              plan === "monthly" ? styles.planOn : ""
            }`}
            onClick={() => setPlan("monthly")}
            aria-pressed={plan === "monthly"}
          >
            <span className={styles.planMain}>
              <span className={styles.radio} aria-hidden>
                <span className={styles.radioDot} />
              </span>
              <span className={styles.planTextWrap}>
                <span className={styles.planNameSm}>Monthly</span>
                <span className={styles.planCoversSm}>one month at a time</span>
              </span>
              <span className={styles.planPriceSm}>
                <span className={styles.priceSm}>{monthlyPrice}</span>
                <span className={styles.pricePerSm}>/mo</span>
              </span>
            </span>
          </button>
        </section>

        {/* CTA */}
        <div className={styles.ctaDock}>
          <button
            className={`${styles.cta} ${committed ? styles.ctaCommitted : ""}`}
            onClick={purchase}
          >
            <span className={styles.ctaLabel}>
              {committed
                ? "Sealing your vault…"
                : "Seal my vault — 7 days free"}
            </span>
            {!committed && (
              <svg
                className={styles.ctaArrow}
                viewBox="0 0 24 24"
                width="18"
                height="18"
                aria-hidden
              >
                <path
                  d="M5 12h13M12 5l7 7-7 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.1"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </button>
          <p className={styles.ctaSub}>{ctaSub}</p>
          <p className={styles.reassure}>
            Your photos are always yours to keep. The eclipse only hides them
            from everyone else — nothing is ever deleted or locked away from
            you.
          </p>

          <div className={styles.legal}>
            <button onClick={() => native.postNativeEvent("already_purchased")}>
              Restore
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("terms")}>
              Terms
            </button>
            <span>·</span>
            <button onClick={() => native.postNativeEvent("privacy")}>
              Privacy
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EclipsePaywall
